import React, { useEffect, useState, type FC } from 'react';
import { Button, InputText, Select, Space, Switchbox } from '@g2u/ui-kit';
import { Helmet } from 'react-helmet-async';
import { observer } from 'mobx-react-lite';
import { Form, Tabs, type TablePaginationConfig } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { Role, type JsonDataItem } from 'types';
import { useTickets } from './hooks/useTickets';
import { useQueryParams } from './hooks/useQueryParams';
import { useTaskLock } from 'hooks/useTaskLock';
import { TicketsTable } from './components';
import { Content } from 'components';
import { RootStore } from 'store';
import styles from './tickets.module.scss';

export const TicketsPage: FC = observer(() => {
  const { getParam, setParams, deleteParam } = useQueryParams();
  const expandedTicketParam = getParam('expanded_id');
  const searchTypeParam = getParam('search_type');
  const searchValueParam = getParam('search_value');
  const { user } = RootStore;
  const [form] = Form.useForm();
  const {
    ticketTypes,
    ticketAssignees,
    tableSearchParams,
    updateSearchParams,
    tablePagination,
    setTablePagination,
    tickets,
    isLoading,
    toggleTaskPin,
    updateTask,
    updateTicket,
    updateTicketDueDate,
    closeTicket,
    reopenTicket,
    isReopened,
    callToCustomer,
  } = useTickets();
  const { removeSoftlock, addSoftlock } = useTaskLock();

  const handleTabChange = (tab: string) => {
    updateSearchParams({ status: tab, page: 1 });
  };
  const handleFormSubmit = (fields: { searchBy: string; searchValue: string; showMyTask?: boolean }) => {
    updateSearchParams({
      searchValue: fields.searchValue,
      filterBy: fields.searchBy,
      userId: fields.showMyTask ? user?.ID : undefined,
      page: 1,
    });
  };

  const handleTableFilter = (action: TicketsTable.FilterAction) => {
    const { kind, id } = action;

    if (kind === 'ticketType' || kind === 'userId') {
      updateSearchParams({ [kind]: id, page: 1 });
      kind === 'userId' && handleChangeAssignee(id);
    }

    if (kind === 'customer_id' || kind === 'technician_id') {
      form.setFieldValue('searchBy', kind);
      form.setFieldValue('searchValue', id.toString());
      updateSearchParams({
        ...tableSearchParams,
        searchValue: id.toString(),
        filterBy: kind,
        page: 1,
      });
    }
  };

  const handleTableExpand = (id: number) => {
    const newId = Number(expandedTicketParam) === id ? null : id;
    newId ? setParams({ expanded_id: newId.toString() }) : deleteParam('expanded_id');
  };

  const handlePaginationChange = (params: TablePaginationConfig) => {
    setTablePagination((prev) => ({ ...prev, ...params }));
    updateSearchParams({ ...params });
  };

  useEffect(() => {
    const initValues: JsonDataItem = {};
    if (searchTypeParam) initValues.searchBy = searchTypeParam;
    if (searchValueParam || expandedTicketParam) initValues.searchValue = searchValueParam || expandedTicketParam;
    form.setFieldsValue(initValues);
  }, []);

  useEffect(() => {
    isReopened && handleTabChange('1');
  }, [isReopened]);

  // sync "My tasks" toggle with table assignee filtering
  const [assigneeFilterId, setAssigneeFilterId] = useState<number | null>(null);
  const handleChangeAssignee = (assigneeId: number | null) => {
    setAssigneeFilterId(assigneeId);
    form.setFieldValue('showMyTask', assigneeId === user?.ID);
  };

  return (
    <Content.$ dataTestId="tickets-page" className={styles.tickets} hasContainer>
      <Helmet>
        <title>Tickets</title>
      </Helmet>

      <div className={styles.tickets}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <Form form={form} layout="vertical" className={styles.tickets__form} onFinish={handleFormSubmit}>
            <Space.$ fullWidth justify={Space.Justify.SpaceBetween} align={Space.Align.Start}>
              <Space.$ compact fullWidth>
                <Select.$
                  name="searchBy"
                  rules={[{}]}
                  dataTestId="search-by-select"
                  className={styles.tickets__formSelect}
                  initialValue={'customer_id'}
                  suffixIcon={undefined}
                  options={[
                    { label: <>Customer ID</>, value: 'customer_id' },
                    { label: <>Technician ID</>, value: 'technician_id' },
                    { label: <>Job ID</>, value: 'service_id' },
                    { label: <>Ticket ID</>, value: 'record_id' },
                  ]}
                />
                <InputText.$
                  fullWidth
                  name="searchValue"
                  initialValue={''}
                  placeholder={'Search tickets'}
                  dataTestId="search-value"
                  noMargin
                  className={styles.tickets__formSearch}
                />
                <Button.$
                  type={Button.Type.Primary}
                  dataTestId="search-submit-btn"
                  onClick={() => null}
                  icon={<SearchOutlined />}
                  htmlType="submit"
                />
              </Space.$>

              <Switchbox.$
                horizontal
                reverse
                initialValue={false}
                name="showMyTask"
                label="Show my tasks only"
                onChange={(value) => {
                  handleChangeAssignee(value ? user?.ID : null);
                  form.submit();
                }}
              />
            </Space.$>
          </Form>
        </Space.$>

        <Tabs
          activeKey={tableSearchParams.status}
          onChange={handleTabChange}
          items={[
            { key: '1', label: 'Open Tickets' },
            { key: '0', label: 'Closed Tickets' },
          ]}
        />
        <TicketsTable.$
          tickets={tickets}
          ticketTypes={ticketTypes}
          ticketAssignees={ticketAssignees}
          expandedRowKeys={expandedTicketParam ? [Number(expandedTicketParam)] : []}
          onExpand={handleTableExpand}
          isLoading={isLoading}
          onReopen={reopenTicket}
          onClose={closeTicket}
          onUpdateTicket={updateTicket}
          onUpdateTask={updateTask}
          onUpdateDueDate={updateTicketDueDate}
          onFilter={handleTableFilter}
          onLock={addSoftlock}
          onUnlock={removeSoftlock}
          onPin={toggleTaskPin}
          onCallToCustomer={callToCustomer}
          pagination={tablePagination}
          onPaginationChange={handlePaginationChange}
          hasLogs={user?.role !== Role.CSS}
          assigneeFilterId={assigneeFilterId}
        />
      </div>
    </Content.$>
  );
});
